import React from 'react';

import { Box } from '@chakra-ui/react';
import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  InputGroup,
  InputRightElement,
  Text,
  TextField,
} from '@firehydrant/design-system';
import { Field } from 'formik';
import PropTypes from 'prop-types';

const FormikTextInput = ({
  customError,
  forceShowError,
  formWidth,
  helperText,
  label,
  labelProps,
  maxLength,
  mb,
  name,
  rightChildren,
  leftIcon,
  type,
  validate,
  isRequired,
  supressComponentErrorMessage,
  ignorePasswordManager = true,
  ...props
}) => {
  return (
    <Field name={name} validate={validate}>
      {({ field, meta }) => {
        const isInvalid =
          ((meta.touched || forceShowError) && meta.error) || customError;
        return (
          <FormControl
            w={formWidth}
            mb={mb}
            isReadOnly={props?.isReadOnly}
            isInvalid={isInvalid}
          >
            {label && (
              <Flex
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <FormLabel
                  color="content.secondary"
                  textStyle="body1"
                  {...labelProps}
                  id={name}
                >
                  {label}
                </FormLabel>
                {isRequired && (
                  <Text
                    as="span"
                    float="right"
                    textStyle="body1"
                    color={isInvalid ? "content.feedback.error.secondary" : "content.tertiary"}
                  >
                    Required
                  </Text>
                )}
              </Flex>
            )}
            <InputGroup>
              {leftIcon && <Box mt={2}>{leftIcon}</Box>}
              <TextField
                onWheel={(e) => {
                  if (type === 'number') {
                    e.target.blur();
                  }
                }}
                maxLength={maxLength}
                type={type}
                bg={props.isReadOnly ? 'surface.secondary' : 'surface.primary'}
                {...(ignorePasswordManager && { 'data-1p-ignore': true })}
                {...field}
                {...props}
              />
              {rightChildren && (
                <InputRightElement>{rightChildren}</InputRightElement>
              )}
            </InputGroup>
            <Flex justifyContent="space-between">
              {helperText && <FormHelperText mt="0">{helperText}</FormHelperText>}
              {maxLength && (
                <Text mt={1} textStyle="caption" mb={0} color="content.tertiary">
                  {`${field.value?.length}/${maxLength}`}
                </Text>
              )}
            </Flex>
            {((!supressComponentErrorMessage &&
              (meta.touched || forceShowError) &&
              meta.error) ||
              customError) && (
              <FormErrorMessage color="content.feedback.error.secondary">
                {meta.error || customError}
              </FormErrorMessage>
            )}
          </FormControl>
        );
      }}
    </Field>
  );
};

FormikTextInput.propTypes = {
  customError: PropTypes.string,
  forceShowError: PropTypes.bool,
  formWidth: PropTypes.string,
  helperText: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  isReadOnly: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  labelProps: PropTypes.shape({}),
  maxLength: PropTypes.number,
  mb: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string.isRequired,
  rightChildren: PropTypes.element,
  type: PropTypes.string,
  validate: PropTypes.func,
  leftIcon: PropTypes.element,
  isRequired: PropTypes.bool,
  supressComponentErrorMessage: PropTypes.bool,
  ignorePasswordManager: PropTypes.bool,
};

FormikTextInput.defaultProps = {
  label: null,
  mb: 3,
  rightChildren: null,
  validate: undefined,
  supressComponentErrorMessage: false,
  ignorePasswordManager: true,
};

export default FormikTextInput;
